import type { MapLibreMap } from 'maplibre-gl';
import { firstSymbolLayerId } from './radar-layers';

/**
 * The basemap dimmer.
 *
 * A colour basemap and the radar palette compete for the same attention: light
 * green precipitation over green landcover, or a warm echo over desert, is
 * genuinely hard to read. The old answer was to make the basemap grey, which
 * costs the viewer the geography they use to place the weather — coastlines,
 * lakes, relief, roads.
 *
 * This is the cheaper trade: keep the colour, push it back. A `background`
 * layer inserted above the basemap's fills darkens everything drawn below it
 * while leaving the radar, the labels and the map's *shapes* intact. Colour
 * survives at a lower value, which is exactly where a basemap belongs.
 *
 * `background` needs no source of its own and covers the viewport by
 * definition, so this costs one layer and no network traffic.
 */

export const BASEMAP_DIM_LAYER_ID = 'basemap-dim';

/** Near-black with the shell's blue cast, so dimming reads as tinted, not grey. */
const DIM_COLOR = '#05080c';

export interface BasemapDimConfig {
  /** 0 leaves the basemap untouched; 1 would be solid ink. */
  opacity: number;
}

/**
 * Mounts the dimmer, or updates its opacity if it's already there.
 *
 * The layer is mounted even at zero opacity, and never removed while the style
 * lives. Both the radar and the coverage scrim insert themselves *before the
 * first symbol layer*, which means whatever was added most recently sits on
 * top. Re-adding the dimmer later — when a viewer drags it up from zero — would
 * therefore place it above the radar and dim the data instead of the map.
 * Mounting once, at style load, fixes the ordering for the session.
 */
export function syncBasemapDim(map: MapLibreMap, config: BasemapDimConfig): void {
  const opacity = clamp(config.opacity);

  if (map.getLayer(BASEMAP_DIM_LAYER_ID)) {
    map.setPaintProperty(BASEMAP_DIM_LAYER_ID, 'background-opacity', opacity);
    return;
  }

  map.addLayer(
    {
      id: BASEMAP_DIM_LAYER_ID,
      type: 'background',
      paint: {
        'background-color': DIM_COLOR,
        'background-opacity': opacity,
      },
    },
    // Below the labels, like everything else we add — a dimmed basemap still
    // needs readable place names.
    firstSymbolLayerId(map),
  );
}

/** Removes the dimmer, e.g. when the overlay stack is torn down. */
export function removeBasemapDim(map: MapLibreMap): void {
  if (map.getLayer(BASEMAP_DIM_LAYER_ID)) map.removeLayer(BASEMAP_DIM_LAYER_ID);
}

function clamp(opacity: number): number {
  if (!Number.isFinite(opacity)) return 0;
  return Math.min(1, Math.max(0, opacity));
}
